// Selectors
import * as ArtSelectors from '~/selectors/ArtSelectors';
import {getIconById} from '~/selectors/CanvasSelectors';
// Action Types
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import ArtTypes from '~/constants/types/ArtTypes';
import IconTypes from '~/constants/types/IconTypes';
// Utils
import {normalizeTagName} from '~/utils/analytics';
import {restingScreenCustomDimension} from '~/reducers/Analytics';
import {
  getSearchTerm,
  hasArtIcon,
  isArtIcon,
  translateBrowsedCategories,
  createCanvasRemoveEvents,
  queryMostSpecificId,
  queryCategoryNameById
} from './utils';
import {isArt} from '~/data/accessors/icon';

export const Art = {
  start: (_, {payload}) => {
    if (!payload?.queryParams?.rs) return null;
    return {
      event: 'lab_art_start',
      art_source: restingScreenCustomDimension(payload.queryParams)
    };
  },
  browse: (state, {payload: location}) => {
    const categoryNames = ArtSelectors.getCategoryNamesJs(state);
    const art_category_id = queryMostSpecificId(location.pathname);
    return {
      event: 'lab_art_browse',
      art_category_path: translateBrowsedCategories(
        ArtSelectors.getBrowsedCategories(state),
        categoryNames
      ),
      art_search_term: null,
      art_has_results: null,
      art_category_id,
      art_category: queryCategoryNameById(art_category_id, categoryNames)
    };
  },
  search: (_, {payload}) => ({
    event: 'lab_art_search',
    art_search_term: payload.category,
    art_has_results: payload.resource.count > 0,
    art_category_path: null,
    art_category: null,
    art_category_id: null
  }),
  add: (state, {payload}) => ({
    event: 'lab_art_add',
    art_id: payload.icon.clipartId,
    art_category_path: translateBrowsedCategories(
      ArtSelectors.getBrowsedCategories(state),
      ArtSelectors.getCategoryNamesJs(state),
      payload.icon.categoryPath
    ),
    art_search_term: getSearchTerm(payload.icon.categoryPath)
  }),
  remove: (state, {payload}) => {
    return createCanvasRemoveEvents(payload, {
      getById: getIconById(state),
      categoryNames: ArtSelectors.getCategoryNamesJs(state),
      targetIconType: 'CLIPART'
    });
  },
  editToolsResetToOriginal: (_, {payload}) => {
    const {source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'reset_to_original',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsSingleColor: (_, {payload, meta}) => {
    const {singleColor, source} = payload;
    const {iconType} = meta;
    if (!source || !isArt(iconType)) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'make_one_color',
      tool_value: singleColor ? 'on' : 'off',
      tool_source: source
    };
  },
  editToolsSpotColor: (state, {payload}) => {
    const {iconId, user, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'edit_colors',
      tool_value: user?.name ? normalizeTagName(user?.name) : null,
      tool_source: source
    };
  },
  editToolsColor: (state, {payload}) => {
    const {color, iconId, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'edit_colors',
      tool_value: color?.name ? normalizeTagName(color?.name) : null,
      tool_source: source
    };
  },
  editToolsChangeArt: (state, {payload}) => {
    const {source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'change',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsCrop: () => {
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'crop',
      tool_value: null,
      tool_source: 'panel'
    };
  },
  editToolsDuplicate: (_, {payload}) => {
    const {source, iconType} = payload;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: iconType,
      tool_name: 'duplicate',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsRemoveWhite: (state, {payload}) => {
    const {iconId, transparency, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'remove_white',
      tool_value: transparency ? 'on' : 'off',
      tool_source: source
    };
  },
  editToolsSize: (_, {payload}) => {
    const {transforms, source} = payload;
    if (!hasArtIcon({transforms})) return null;
    const mapAdjustSources = {
      drag: 'canvas'
    };
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'resize',
      tool_value: null,
      tool_source: mapAdjustSources[source] || source
    };
  },
  editToolsRotation: (state, {payload}) => {
    const {iconId, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'rotate',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsPositionCenter: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasArtIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'center',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerForward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasArtIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'layer_forward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerBackward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasArtIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'layer_backward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsHorizontalFlip: (state, {payload}) => {
    const {flip, iconId, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'flip_h',
      tool_value: flip,
      tool_source: source
    };
  },
  editToolsVerticalFlip: (state, {payload}) => {
    const {flip, iconId, source} = payload;
    if (!source || !isArtIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'flip_v',
      tool_value: flip,
      tool_source: source
    };
  },
  editToolsInvertColors: (_, {payload}) => {
    const {icon, isInverted, source} = payload;
    if (!source || !isArt(icon?.type)) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'art',
      tool_name: 'invert_colors',
      tool_value: isInverted ? 'on' : 'off',
      tool_source: source
    };
  }
};

const ArtEvents = {
  [AnalyticsTypes.ADD_ART]: Art.start,
  [ArtTypes.SEARCH_LOADED]: Art.search,
  [ArtTypes.ADD_ART]: Art.add,
  [IconTypes.REMOVE]: Art.remove,
  [IconTypes.CROP]: Art.editToolsCrop,
  [IconTypes.DUPLICATE]: Art.editToolsDuplicate,
  [IconTypes.RESET_CLIPART_COLORS]: Art.editToolsResetToOriginal,
  [IconTypes.UPDATE_SINGLE_COLOR]: Art.editToolsSingleColor,
  [IconTypes.CHANGE_COLOR]: Art.editToolsColor,
  [ArtTypes.CHANGE_SPOT_COLOR]: Art.editToolsSpotColor,
  [AnalyticsTypes.ART_SWAP]: Art.editToolsChangeArt,
  [ArtTypes.UPDATE_TRANSPARENCY]: Art.editToolsRemoveWhite,
  [IconTypes.RESIZE]: Art.editToolsSize,
  [IconTypes.ROTATE]: Art.editToolsRotation,
  [IconTypes.CENTER]: Art.editToolsPositionCenter,
  [IconTypes.FORWARD]: Art.editToolsLayerForward,
  [IconTypes.BACKWARD]: Art.editToolsLayerBackward,
  [IconTypes.UPDATE_HORIZONTAL_FLIP]: Art.editToolsHorizontalFlip,
  [IconTypes.UPDATE_VERTICAL_FLIP]: Art.editToolsVerticalFlip,
  [IconTypes.TOGGLE_INVERTED_CLIPART]: Art.editToolsInvertColors
};

export default ArtEvents;
